"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { BrandLockup } from "@/components/brand-lockup";
import { nav, site } from "@/lib/data";

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

const actionBtn =
  "inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-4 text-sm font-semibold transition-colors";

function ProfileIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 19.5c1.6-3.2 4-4.8 6.5-4.8s4.9 1.6 6.5 4.8" />
    </svg>
  );
}

function LoginMenu({
  align = "right",
  onNavigate,
}: {
  align?: "left" | "right";
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-ink transition-colors hover:border-red/40 hover:bg-red-soft hover:text-red"
        aria-label="Login options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <ProfileIcon className="h-5 w-5" />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Choose login type"
          className={`absolute z-50 mt-2 w-56 rounded-2xl border border-line bg-surface p-2 shadow-[var(--shadow-card-hover)] ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted">
            Continue as
          </p>
          <Link
            href={site.dealerLogin}
            role="menuitem"
            className="flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold text-ink transition-colors hover:bg-red-soft hover:text-red"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          >
            Dealer Login
          </Link>
          <Link
            href={site.customerLogin}
            role="menuitem"
            className="flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold text-ink transition-colors hover:bg-red-soft hover:text-red"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          >
            Customer Login
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export function Header() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-line/80 bg-surface/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
        <div className="min-w-0 shrink">
          <BrandLockup
            logoClassName="h-12 w-auto object-contain sm:h-14"
            copyClassName="flex min-w-0 flex-col justify-center leading-tight"
            priority
          />
        </div>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {nav.map((item) => {
            const active = isActive(pathname, item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`inline-flex h-11 items-center whitespace-nowrap rounded-full px-3 text-sm font-medium transition-colors ${
                  active
                    ? "bg-red-soft text-red"
                    : "text-charcoal hover:bg-background hover:text-red"
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          <div className="ml-2 flex shrink-0 items-center gap-2 border-l border-line pl-3">
            <Link
              href="/contact"
              className={`${actionBtn} bg-red text-white hover:bg-red-dark`}
            >
              Get a Quote
            </Link>
            <LoginMenu />
          </div>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <LoginMenu align="right" onNavigate={() => setOpen(false)} />
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-line bg-surface"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((value) => !value)}
          >
            <span className="sr-only">Menu</span>
            <span className="flex flex-col gap-1.5" aria-hidden>
              <span className="block h-0.5 w-5 bg-ink" />
              <span className="block h-0.5 w-5 bg-ink" />
              <span className="block h-0.5 w-5 bg-ink" />
            </span>
          </button>
        </div>
      </div>

      {open ? (
        <div
          id="mobile-nav"
          className="border-t border-line bg-surface/95 px-4 py-4 backdrop-blur-xl lg:hidden"
        >
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {nav.map((item) => {
              const active = isActive(pathname, item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={`inline-flex min-h-12 items-center rounded-xl px-3 text-base font-medium ${
                    active ? "bg-red-soft text-red" : "text-charcoal"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              href="/contact"
              onClick={() => setOpen(false)}
              className={`${actionBtn} mt-3 w-full bg-red text-white hover:bg-red-dark`}
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
